import { gettext } from '@eventbrite/i18n';
import { css } from 'emotion';
import React, { Fragment } from 'react';
import Modal from '../../modal';
import { useTheme } from '../../theme-context';
import HoldLine from './hold-line';

interface IModalManageHolds {
    onHeldQuantityChange: (id: string, quantity: string) => void;
    Info: {
        title: string;
        ticketType: string;
        availableQuantityLabel: string;
        totalHolds: string;
    };
    variants: {
        id: string;
        name: string;
        soldQuantity: string;
        heldQuantity: string;
    }[];
    handleClose: () => void;
    isShown: boolean;
    onPrimaryAction: () => void;
    disablePrimaryAction?: boolean;
}

const ModalManageHolds = ({
    handleClose,
    Info,
    isShown,
    onHeldQuantityChange,
    variants,
    onPrimaryAction,
    disablePrimaryAction,
}: IModalManageHolds): React.ReactElement => {
    const { title, ticketType, availableQuantityLabel, totalHolds } =
        Info || {};
    const { getFont, styles } = useTheme();
    const sectionInfoHeaderClass = css({
        fontFamily: getFont('desktop/body/small').fontFamily,
        fontWeight: getFont('desktop/body/small').fontWeight,
        fontSize: getFont('desktop/body/small').fontSize,
        marginBottom: '4px',
    });
    const sectionInfoDataClass = css({
        fontFamily: getFont('desktop/body/medium').fontFamily,
        fontWeight: getFont('desktop/body/medium').fontWeight,
        fontSize: getFont('desktop/body/medium').fontSize,
        paddingRight: '6px',
    });
    const sectionInfoDataWithOverflowClass = `${sectionInfoDataClass}
        ${css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `}`;

    return (
        <Modal
            isShown={isShown}
            title={gettext('Manage Holds')}
            onClose={handleClose}
            showCloseButton={true}
            onPrimaryAction={onPrimaryAction}
            primaryActionLabel={gettext('Update holds')}
            disablePrimaryAction={disablePrimaryAction}
        >
            <div
                className={css({
                    padding: '24px 68px',
                })}
            >
                <div
                    className={css({
                        marginBottom: '22px',
                    })}
                >
                    <div
                        className={css({
                            display: 'grid',
                            gridTemplateColumns: '3fr repeat(2, 2fr) 1fr;',
                        })}
                    >
                        <div className={sectionInfoHeaderClass}>
                            {gettext('Section')}
                        </div>
                        <div className={sectionInfoHeaderClass}>
                            {gettext('Ticket type')}
                        </div>
                        <div className={sectionInfoHeaderClass}>
                            {gettext('Available')}
                        </div>
                        <div className={sectionInfoHeaderClass}>
                            {gettext('Total Holds')}
                        </div>
                        <div
                            className={sectionInfoDataWithOverflowClass}
                            data-testid="section-title"
                            title={title}
                        >
                            {title}
                        </div>
                        <div
                            className={sectionInfoDataWithOverflowClass}
                            data-testid="ticket-type"
                            title={ticketType}
                        >
                            {ticketType}
                        </div>
                        <div
                            className={sectionInfoDataClass}
                            data-testid="available-quantity"
                            title={availableQuantityLabel}
                        >
                            {availableQuantityLabel}
                        </div>
                        <div
                            className={sectionInfoDataClass}
                            data-testid="total-holds"
                            title={totalHolds}
                        >
                            {totalHolds}
                        </div>
                    </div>
                </div>

                <div
                    className={css({
                        display: 'grid',
                        gridTemplateColumns: '2fr 2fr 1fr;',
                    })}
                >
                    <div
                        className={css({
                            fontFamily: getFont('desktop/body/medium')
                                .fontFamily,
                            fontWeight: getFont('desktop/body/medium')
                                .fontWeight,
                            fontSize: getFont('desktop/body/medium').fontSize,
                            padding: '14px 16px',
                            backgroundColor: styles.colors['grey-01'].color,
                        })}
                    >
                        {gettext('Hold type')}
                    </div>
                    <div
                        className={css({
                            fontFamily: getFont('desktop/body/medium')
                                .fontFamily,
                            fontWeight: getFont('desktop/body/medium')
                                .fontWeight,
                            fontSize: getFont('desktop/body/medium').fontSize,
                            padding: '14px 16px',
                            backgroundColor: styles.colors['grey-01'].color,
                        })}
                    >
                        {gettext('Sold')}
                    </div>
                    <div
                        className={css({
                            fontFamily: getFont('desktop/body/medium')
                                .fontFamily,
                            fontWeight: getFont('desktop/body/medium')
                                .fontWeight,
                            fontSize: getFont('desktop/body/medium').fontSize,
                            padding: '14px 16px',
                            backgroundColor: styles.colors['grey-01'].color,
                        })}
                    >
                        {gettext('Held')}
                    </div>
                    {variants.map(
                        ({ id, name, soldQuantity, heldQuantity }) => (
                            <Fragment key={id}>
                                <HoldLine
                                    onHeldQuantityChange={onHeldQuantityChange}
                                    info={{
                                        id,
                                        name,
                                        soldQuantity,
                                        heldQuantity,
                                    }}
                                />
                            </Fragment>
                        ),
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ModalManageHolds;
